import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaLinkedin, FaTwitter, FaEnvelope } from 'react-icons/fa';
import './Home.css';

const Home = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const profileRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const texts = useMemo(() => [
    { title: 'Student Developer', color: '#00d4ff', icon: '💻' },
    { title: 'AI & Data Science Student', color: '#a855f7', icon: '🤖' },
    { title: 'Co-founder at AGR Creative Tech Studio', color: '#d6ff3e', icon: '🚀' },
    { title: 'MERN Stack Learner', color: '#f97316', icon: '⚡' }
  ], []);

  // Cycle through roles
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % texts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [texts.length]);

  // Mouse parallax for profile image
  const handleMouseMove = useCallback((e) => {
    if (!profileRef.current) return;
    const rect = profileRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = (e.clientX - centerX) / rect.width;
    const y = (e.clientY - centerY) / rect.height;
    setMousePos({ x: x * 15, y: y * 15 });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const socialLinks = [
    { icon: FaGithub, url: 'https://github.com/lakshmananr', label: 'GitHub' },
    { icon: FaLinkedin, url: 'https://www.linkedin.com/in/lakshmananr', label: 'LinkedIn' },
    { icon: FaTwitter, url: 'https://twitter.com/lakshmananr', label: 'Twitter' },
    { icon: FaEnvelope, url: 'mailto:lakshmanan.rofficial7@gmail.com', label: 'Email' }
  ];

  const currentRole = texts[currentTextIndex];

  return (
    <section className="home" id="home">
      <div className="container">
        <div className="home-content">
          <motion.div
            className="home-text"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.p
              className="home-greeting"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Hello, I'm
            </motion.p>

            <h1 className="home-title">
              <motion.span
                className="title-first"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
              >
                Lakshmanan
              </motion.span>
              <motion.span
                className="highlight"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, duration: 0.5, type: 'spring', stiffness: 200 }}
              >
                {' '}R
              </motion.span>
            </h1>

            {/* Animated Role Card */}
            <div className="role-container">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTextIndex}
                  className="role-card"
                  style={{
                    '--role-color': currentRole.color,
                    borderColor: `${currentRole.color}40`,
                    boxShadow: `0 0 30px ${currentRole.color}15, inset 0 0 30px ${currentRole.color}08`
                  }}
                  initial={{ opacity: 0, y: 30, scale: 0.9, rotateX: -15 }}
                  animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                  exit={{ opacity: 0, y: -30, scale: 0.9, rotateX: 15 }}
                  transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                >
                  <span className="role-icon">{currentRole.icon}</span>
                  <span className="role-text" style={{ color: currentRole.color }}>
                    {currentRole.title}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Role Indicator Dots */}
              <div className="role-indicators">
                {texts.map((_, index) => (
                  <motion.div
                    key={index}
                    className={`role-dot ${index === currentTextIndex ? 'active' : ''}`}
                    style={{
                      backgroundColor: index === currentTextIndex ? texts[index].color : 'rgba(255,255,255,0.2)'
                    }}
                    animate={index === currentTextIndex ? { scale: [1, 1.3, 1] } : {}}
                    transition={{ duration: 0.4 }}
                  />
                ))}
              </div>
            </div>

            <motion.p
              className="home-description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              20 | Student | AI & Data Science | JavaScript | React | Node.js |
              Python | Learning | Co-founder at AGR Creative Tech Studio
            </motion.p>

            <motion.div
              className="home-buttons"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
            >
              <MagneticButton
                className="btn"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Contact Me
              </MagneticButton>
              <MagneticButton
                className="btn btn-outline"
                onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              >
                View Portfolio
              </MagneticButton>
            </motion.div>

            <div className="home-social">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.4 + index * 0.1 }}
                >
                  <social.icon />
                  <span className="sr-only">{social.label}</span>
                </motion.a>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="home-image"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div
              className="profile-image"
              ref={profileRef}
              style={{
                transform: `perspective(800px) rotateY(${mousePos.x * 0.5}deg) rotateX(${-mousePos.y * 0.5}deg)`
              }}
            >
              {/* Animated gradient ring */}
              <div className="profile-ring" />
              <div className="profile-ring profile-ring-2" />
              <img
                src="/lakshmanan-profile.jpg.jpg"
                alt="Lakshmanan R"
                className="profile-img"
              />
              {/* Floating accent dots */}
              <div className="profile-accent accent-1" />
              <div className="profile-accent accent-2" />
              <div className="profile-accent accent-3" />
            </div>
          </motion.div>
        </div>

        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          <div className="scroll-text">Scroll down</div>
          <div className="scroll-arrow">
            <motion.span
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            >
              ↓
            </motion.span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Magnetic Button Component
const MagneticButton = ({ children, className, onClick }) => {
  const btnRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const handleMouse = useCallback((e) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distX = e.clientX - centerX;
    const distY = e.clientY - centerY;
    const distance = Math.sqrt(distX * distX + distY * distY);
    const maxDist = 120;

    if (distance < maxDist) {
      const strength = 1 - distance / maxDist;
      setOffset({
        x: distX * strength * 0.3,
        y: distY * strength * 0.3
      });
    } else {
      setOffset({ x: 0, y: 0 });
    }
  }, []);

  const handleLeave = useCallback(() => {
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, [handleMouse]);

  return (
    <motion.button
      ref={btnRef}
      className={className}
      onClick={onClick}
      onMouseLeave={handleLeave}
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: 'spring', stiffness: 150, damping: 15 }}
    >
      {children}
    </motion.button>
  );
};

export default Home;